import { Navigate, useLocation } from 'react-router-dom';
import { useSubscriptionStatus } from '@/hooks/use-subscription';
import { hasActivePlatformAdminTenantViewAccess } from '@/lib/admin-tenant-view';

/** Wraps protected routes. When the subscription is expired, redirect to
 *  /paywall — except when the user is already on /paywall or /settings/billing
 *  (so they can actually pay). Renders a tiny placeholder while loading.
 *
 *  Behavior is intentionally simple: hard block (per Khalil's pick during
 *  planning). Soft block / read-only would require a much larger surface area
 *  of guards and isn't worth it for a single-tier product.
 */
export function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { isLoading, expired } = useSubscriptionStatus();
  const location = useLocation();
  const hasAdminTenantViewAccess = hasActivePlatformAdminTenantViewAccess();

  // Routes that must work even when blocked, otherwise the user can't pay.
  const exempt = ['/paywall', '/settings/billing'];
  const onExemptRoute = exempt.some((p) => location.pathname.startsWith(p));

  if (hasAdminTenantViewAccess) {
    return <>{children}</>;
  }

  if (isLoading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">...</div>;
  }

  if (expired && !onExemptRoute) {
    return <Navigate to="/paywall" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
